import { agGridDatasourceQueryKey } from '@/lib/createAgGridDatasource';
import { useQueryClient } from '@tanstack/react-query';
import type { AgGridReact } from 'ag-grid-react';
import { type RefObject, useCallback } from 'react';

/**
 * Returns a stable `refresh` callback that, when called, will:
 *
 * 1. Invalidate every TanStack Query cache entry for the given grid URL
 *    (all pages, sort orders, and filter combinations).
 * 2. Purge AG Grid's own server-side block cache and trigger a full reload,
 *    so the grid immediately re-calls `getRows` with fresh data.
 *
 * This is the intended companion to the `staleTime` option in
 * `createAgGridDatasource`.  Because that default is set to 1 day, navigating
 * back to a table is always instant — and this hook gives you an easy way to
 * wire up an explicit "Refresh" button whenever you need one.
 *
 * @param url    - The endpoint URL passed to `createAgGridDatasource` for this
 *                 grid. Used to scope cache invalidation to only this table.
 * @param gridRef - Ref to the `AgGridReact` instance whose data should be
 *                  refreshed.
 *
 * @returns A stable `refresh()` callback safe to pass directly to a button's
 *          `onClick` handler or call programmatically.
 *
 * @example
 * ```tsx
 * const gridRef = useRef<AgGridReact<UserPublic>>(null);
 *
 * const refresh = useAgGridDatasourceRefresh(
 *   userQueryKeys.endpoints.globalSearch(),
 *   gridRef,
 * );
 *
 * <Button onPress={refresh}>
 *   <RefreshCw />
 *   {t('users.refresh')}
 * </Button>
 *
 * <Table ref={gridRef} rowModelType="serverSide" ... />
 * ```
 */
export function useAgGridDatasourceRefresh<TRow = unknown>(
  url: string,
  gridRef: RefObject<AgGridReact<TRow> | null>,
): () => void {
  const queryClient = useQueryClient();

  return useCallback(() => {
    // Step 1 — mark all cached pages/sorts/filters for this URL as stale.
    // Using the two-element prefix key matches every entry whose key starts
    // with ['ag-grid-datasource', url], regardless of the request body that
    // was used for pagination/filtering.
    queryClient.invalidateQueries({
      queryKey: agGridDatasourceQueryKey(url),
    });

    // Step 2 — purge AG Grid's own in-memory block cache and force a reload.
    // `purge: true` clears all cached row blocks so every visible row is
    // re-requested via getRows, which will now miss the TQ cache (step 1)
    // and fetch fresh data from the server.
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, [queryClient, url, gridRef]);
}
